import { getTasksRescheduleUpdates } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import {
  DatepickerWithOptions,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';
import { initialDateFormat } from 'shared/types/date-format-options';
import { Task } from 'shared/types/task';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import * as Styled from './reschedule-button.style';

export type RescheduleButtonProps = {
  tasks: Task[];
};

export const RescheduleButton: React.FC<RescheduleButtonProps> = React.memo(
  ({ tasks }) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const today = useToday();
    const user = useUser();
    const { menuOpen, openMenu, closeMenu } = useOpenMenu();

    const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

    const { submit: updateTasks } = useUpdateTasks();

    const onChangeDate = (date?: Date) => {
      if (!date) {
        return;
      }

      updateTasks(
        getTasksRescheduleUpdates(
          tasks,
          { startDate: date },
          { today, weekStartsOn: getWeekStartsOn(user) },
        ),
      );

      closeMenu();
    };

    return (
      <Styled.Container onClick={stopPropagation} ref={containerRef}>
        <Button
          variant={Variants.Text}
          size={Sizes.Small}
          color={Colors.Inherit}
          onClick={openMenu}
        >
          {t('general.sections.overdue.reschedule.button.label')}
        </Button>

        {menuOpen && (
          <DatepickerWithOptions
            referenceElement={containerRef}
            value={undefined}
            onChange={onChangeDate}
            dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
            weekStartsOn={getWeekStartsOn(user)}
            icon={IconOptions.Calendar}
            onOutside={closeMenu}
          />
        )}
      </Styled.Container>
    );
  },
);
